// Visualización del bracket eliminatorio del Mundial
// Simula un tablero de marcador deportivo con líneas de conexión animadas
import { useState, useMemo } from 'react'
import { motion } from 'framer-motion'
import { Trophy, ChevronRight } from 'lucide-react'
import Badge from '../ui/Badge'
import Flag from '../ui/Flag'

// Estructura de las rondas eliminatorias oficiales del Mundial 2026
const rounds = [
  { key: 'dieciseisavos', label: 'Dieciseisavos', matchCount: 16 },
  { key: 'octavos', label: 'Octavos', matchCount: 8 },
  { key: 'cuartos', label: 'Cuartos', matchCount: 4 },
  { key: 'semifinal', label: 'Semifinales', matchCount: 2 },
  { key: 'tercer_puesto', label: '3° Puesto', matchCount: 1 },
  { key: 'final', label: 'Final', matchCount: 1 },
]

// Componente de un partido individual dentro del bracket
function MatchCard({ match, roundKey, onSelectWinner, isEditable }) {
  const { teamA, teamB, winner } = match

  return (
    <div className="glass rounded-lg overflow-hidden min-w-[180px]">
      {/* Equipo A */}
      <button
        onClick={() => isEditable && teamA && onSelectWinner(teamA)}
        disabled={!isEditable || !teamA}
        className={`
          w-full flex items-center gap-2 px-3 py-2 text-left text-sm
          transition-all duration-200 border-b border-border-subtle
          ${!teamA ? 'opacity-30' : 'cursor-pointer'}
          ${winner?.id === teamA?.id && winner ? 'bg-cyan/10 text-cyan font-semibold' : 'text-text-primary hover:bg-surface-card-hover'}
        `}
      >
        <span className="flex-1 truncate flex items-center">
          {teamA ? (
            <>
              <Flag code={teamA.bandera} className="w-4 h-3 mr-1.5 object-cover rounded shadow-sm shrink-0" />
              <span>{teamA.nombre}</span>
            </>
          ) : (
            'Por definir'
          )}
        </span>
        {winner?.id === teamA?.id && winner && (
          <motion.div initial={{ scale: 0 }} animate={{ scale: 1 }}>
            <ChevronRight className="w-3 h-3 text-cyan" />
          </motion.div>
        )}
      </button>

      {/* Equipo B */}
      <button
        onClick={() => isEditable && teamB && onSelectWinner(teamB)}
        disabled={!isEditable || !teamB}
        className={`
          w-full flex items-center gap-2 px-3 py-2 text-left text-sm
          transition-all duration-200
          ${!teamB ? 'opacity-30' : 'cursor-pointer'}
          ${winner?.id === teamB?.id && winner ? 'bg-cyan/10 text-cyan font-semibold' : 'text-text-primary hover:bg-surface-card-hover'}
        `}
      >
        <span className="flex-1 truncate flex items-center">
          {teamB ? (
            <>
              <Flag code={teamB.bandera} className="w-4 h-3 mr-1.5 object-cover rounded shadow-sm shrink-0" />
              <span>{teamB.nombre}</span>
            </>
          ) : (
            'Por definir'
          )}
        </span>
        {winner?.id === teamB?.id && winner && (
          <motion.div initial={{ scale: 0 }} animate={{ scale: 1 }}>
            <ChevronRight className="w-3 h-3 text-cyan" />
          </motion.div>
        )}
      </button>
    </div>
  )
}

export default function BracketView({ predictions, onUpdate }) {
  const bracket = predictions || {}
  const [activeRound, setActiveRound] = useState('dieciseisavos')

  // Generar partidos para la ronda activa
  const getMatchesForRound = (roundKey) => {
    const roundData = bracket[roundKey] || []
    const round = rounds.find(r => r.key === roundKey)
    const matchCount = round?.matchCount || 0

    return Array.from({ length: matchCount }, (_, i) => ({
      id: `${roundKey}-${i}`,
      teamA: roundData[i]?.teamA || null,
      teamB: roundData[i]?.teamB || null,
      winner: roundData[i]?.winner || null,
    }))
  }

  // Manejar selección de ganador en un partido
  const handleSelectWinner = (roundKey, matchIndex, winner) => {
    const currentRound = bracket[roundKey] || []
    const updatedRound = [...currentRound]

    // Asegurar que el array tiene suficientes elementos
    while (updatedRound.length <= matchIndex) {
      updatedRound.push({ teamA: null, teamB: null, winner: null })
    }

    updatedRound[matchIndex] = {
      ...updatedRound[matchIndex],
      winner,
    }

    let updatedBracket = { ...bracket, [roundKey]: updatedRound }

    // Propagar el ganador a la siguiente ronda
    if (roundKey === 'semifinal') {
      // Los ganadores de las semifinales van a la final
      // Los perdedores van al tercer puesto
      const finalRoundData = [...(updatedBracket['final'] || [])]
      const thirdRoundData = [...(updatedBracket['tercer_puesto'] || [])]

      while (finalRoundData.length < 1) finalRoundData.push({ teamA: null, teamB: null, winner: null })
      while (thirdRoundData.length < 1) thirdRoundData.push({ teamA: null, teamB: null, winner: null })

      const match = updatedRound[matchIndex]
      const loser = winner.id === match.teamA?.id ? match.teamB : match.teamA

      if (matchIndex === 0) {
        finalRoundData[0] = { ...finalRoundData[0], teamA: winner }
        if (loser) thirdRoundData[0] = { ...thirdRoundData[0], teamA: loser }
      } else {
        finalRoundData[0] = { ...finalRoundData[0], teamB: winner }
        if (loser) thirdRoundData[0] = { ...thirdRoundData[0], teamB: loser }
      }

      updatedBracket['final'] = finalRoundData
      updatedBracket['tercer_puesto'] = thirdRoundData
    } else {
      const nextRound = getNextRound(roundKey)
      if (nextRound && roundKey !== 'final' && roundKey !== 'tercer_puesto') {
        const nextRoundData = [...(updatedBracket[nextRound] || [])]
        const nextMatchIndex = Math.floor(matchIndex / 2)

        while (nextRoundData.length <= nextMatchIndex) {
          nextRoundData.push({ teamA: null, teamB: null, winner: null })
        }

        if (matchIndex % 2 === 0) {
          nextRoundData[nextMatchIndex] = { ...nextRoundData[nextMatchIndex], teamA: winner }
        } else {
          nextRoundData[nextMatchIndex] = { ...nextRoundData[nextMatchIndex], teamB: winner }
        }

        updatedBracket[nextRound] = nextRoundData
      }
    }

    onUpdate(updatedBracket)
  }

  // Obtener la ronda siguiente
  const getNextRound = (currentRound) => {
    const roundOrder = ['dieciseisavos', 'octavos', 'cuartos', 'semifinal']
    const idx = roundOrder.indexOf(currentRound)
    return idx < roundOrder.length - 1 ? roundOrder[idx + 1] : null
  }

  // Contar predicciones completadas por ronda
  const getCompletionForRound = (roundKey) => {
    const matches = getMatchesForRound(roundKey)
    const completed = matches.filter(m => m.winner).length
    return { completed, total: matches.length }
  }

  return (
    <div>
      {/* Encabezado */}
      <div className="mb-6">
        <h3 className="text-xl font-display font-bold text-text-primary mb-2">
          Fase Eliminatoria
        </h3>
        <p className="text-sm text-text-secondary">
          Selecciona al ganador de cada partido. Los ganadores avanzan automáticamente a la siguiente ronda.
        </p>
      </div>

      {/* Selector de rondas (tabs) */}
      <div className="flex gap-2 mb-6 overflow-x-auto pb-2">
        {rounds.map(round => {
          const { completed, total } = getCompletionForRound(round.key)
          const isActive = activeRound === round.key

          return (
            <button
              key={round.key}
              onClick={() => setActiveRound(round.key)}
              className={`
                relative flex items-center gap-2 px-4 py-2 rounded-xl text-sm font-medium font-display
                whitespace-nowrap transition-all duration-200
                ${isActive
                  ? 'bg-cyan text-navy-dark'
                  : 'glass text-text-secondary hover:text-text-primary'
                }
              `}
            >
              {round.label}
              {completed > 0 && (
                <Badge
                  variant={completed === total ? 'success' : 'cyan'}
                  size="sm"
                >
                  {completed}/{total}
                </Badge>
              )}
            </button>
          )
        })}
      </div>

      {/* Vista del bracket: partidos de la ronda seleccionada */}
      <div className="relative">
        {/* Partidos */}
        <div className={`grid gap-3 ${
          activeRound === 'dieciseisavos' ? 'grid-cols-1 sm:grid-cols-2 lg:grid-cols-4' :
          activeRound === 'octavos' ? 'grid-cols-1 sm:grid-cols-2 lg:grid-cols-4' :
          activeRound === 'cuartos' ? 'grid-cols-1 sm:grid-cols-2 lg:grid-cols-4' :
          activeRound === 'semifinal' ? 'grid-cols-1 sm:grid-cols-2 max-w-2xl mx-auto' :
          'grid-cols-1 max-w-sm mx-auto'
        }`}>
          {getMatchesForRound(activeRound).map((match, i) => (
            <motion.div
              key={match.id}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: i * 0.05 }}
            >
              {activeRound === 'final' && (
                <div className="text-center mb-3">
                  <Trophy className="w-8 h-8 text-warning mx-auto mb-1" />
                  <p className="text-xs text-text-muted uppercase tracking-wider font-display">Final</p>
                </div>
              )}
              {activeRound === 'tercer_puesto' && (
                <div className="text-center mb-3">
                  <p className="text-xs text-text-muted uppercase tracking-wider font-display">Tercer Puesto</p>
                </div>
              )}
              <MatchCard
                match={match}
                roundKey={activeRound}
                onSelectWinner={(winner) => handleSelectWinner(activeRound, i, winner)}
                isEditable={true}
              />
            </motion.div>
          ))}
        </div>

        {/* Campeón */}
        {bracket.final?.[0]?.winner && (
          <motion.div
            initial={{ opacity: 0, scale: 0.8 }}
            animate={{ opacity: 1, scale: 1 }}
            className="mt-8 text-center"
          >
            <div className="inline-flex flex-col items-center gap-2 p-6 glass-cyan rounded-2xl glow-cyan-lg">
              <Trophy className="w-10 h-10 text-warning" />
              <p className="text-xs text-text-muted uppercase tracking-wider font-display">Tu Campeón</p>
              <p className="text-xl font-display font-bold text-cyan text-glow-cyan flex items-center justify-center gap-1.5">
                <Flag code={bracket.final[0].winner.bandera} className="w-6 h-4 object-cover rounded shadow-sm shrink-0" />
                <span>{bracket.final[0].winner.nombre}</span>
              </p>
            </div>
          </motion.div>
        )}
      </div>
    </div>
  )
}
